import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { triggerEdiExtract } from "../../../utils/apiUtils";
import { useEdiExtractStatus } from "./useEdiExtractStatus";
import EdiExtractSelfServiceDocs from "./EdiExtractSelfServiceDocs";
import EdiExtractStatusCard from "./EdiExtractStatusCard";
import EdiExtractConfirmModal from "./EdiExtractConfirmModal";
import EdiExtractHistoryTable from "./EdiExtractHistoryTable";
import "./EdiExtractSelfService.scss";

const EdiExtractSelfService = () => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    // undefined = run every job; an array = retry only those job names
    const [pendingJobs, setPendingJobs] = useState(undefined);
    const queryClient = useQueryClient();
    const status = useEdiExtractStatus();

    const triggerMutation = useMutation({
        mutationFn: triggerEdiExtract,
        onSuccess: () => {
            setConfirmOpen(false);
            toast.success("EDI extract started");
            queryClient.invalidateQueries({ queryKey: ["edi-extract-status"] });
            queryClient.invalidateQueries({ queryKey: ["edi-extract-history"] });
        },
        onError: (error) => {
            setConfirmOpen(false);
            toast.error(
                error?.cause?.error?.errorCode === "409"
                    ? "Already running — wait for it to finish before starting another."
                    : "Couldn't start the extract. Try again."
            );
            queryClient.invalidateQueries({ queryKey: ["edi-extract-status"] });
        },
    });

    const openConfirm = (jobs) => {
        setPendingJobs(jobs);
        setConfirmOpen(true);
    };

    return (
        <div className="container">
            <p className="title">EDI Extract Self-Service</p>
            <p className="sub-title">Run the daily EDI extract jobs by hand</p>

            <div className="edi-extract-selfservice__layout">
                <div>
                    <EdiExtractSelfServiceDocs />

                    <EdiExtractStatusCard
                        isError={status.isError}
                        isFetching={status.isFetching}
                        state={status.state}
                        failed={status.failed}
                        disabled={status.disabled}
                        data={status.data}
                        failedJobs={status.failedJobs}
                        canRetry={status.canRetry}
                        onRetry={status.refetch}
                        onTriggerClick={() => openConfirm(undefined)}
                        onRetryFailedClick={() => openConfirm(status.failedJobs)}
                    />

                    <EdiExtractConfirmModal
                        isOpen={confirmOpen}
                        onClose={() => setConfirmOpen(false)}
                        isPending={triggerMutation.isPending}
                        jobs={pendingJobs}
                        onConfirm={(jobs) => triggerMutation.mutate(jobs)}
                    />
                </div>

                <aside>
                    <EdiExtractHistoryTable />
                </aside>
            </div>
        </div>
    );
};

export default EdiExtractSelfService;
